import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatedShield, Wordmark } from "@workspace/brand-react";
import {
  useGetBrandPortalStatus,
  useUnlockBrandPortal,
  useLockBrandPortal,
  getGetBrandPortalStatusQueryKey,
} from "@workspace/api-client-react";

/** Read the `fid_csrf` CSRF token from the browser cookie jar. */
function readCsrfToken(): string {
  try {
    const match = document.cookie
      .split(";")
      .find((c) => c.trim().startsWith("fid_csrf="));
    return match
      ? decodeURIComponent(match.trim().slice("fid_csrf=".length))
      : "";
  } catch {
    return "";
  }
}
import { Seo } from "@/components/Seo";
import { Section, Eyebrow } from "@/components/Section";
import { apiUrl } from "@/lib/utils";
import { Copy, Check, Download, LockKeyhole, LogOut } from "lucide-react";

const SWATCHES = [
  { name: "Forest", hex: "#1E4A40", hsl: "168 32% 17%", token: "--brand-forest", textOnDark: true },
  { name: "Brass", hex: "#C9A961", hsl: "42 49% 58%", token: "--brand-brass", textOnDark: false },
  { name: "Charcoal", hex: "#2A2826", hsl: "36 4% 16%", token: "--brand-charcoal", textOnDark: true },
  { name: "Bone", hex: "#F4F1E8", hsl: "50 25% 95%", token: "--brand-bone", textOnDark: false },
  { name: "Sand", hex: "#E8E4D8", hsl: "40 18% 89%", token: "--brand-sand", textOnDark: false },
  { name: "Slate", hex: "#5F5C57", hsl: "43 4% 36%", token: "--brand-slate", textOnDark: true },
] as const;

const MOTION_BEATS = [
  { beat: "static" as const, label: "static", caption: "Brand mark at rest. Use anywhere typography sits — headers, footers, tight UI chrome." },
  { beat: "ambient" as const, label: "ambient", caption: "Slow brass dash on the traces. Use on dashboards, sidebars, loaders." },
  { beat: "handshake" as const, label: "handshake", caption: "Single inward + outward pulse. Use on successful actions and confirmations." },
  { beat: "power-on" as const, label: "power-on", caption: "Frame draws, die appears, traces light up. First-load splashes only." },
  { beat: "data-flow" as const, label: "data-flow", caption: "Labelled packets encrypting into the chip. Marketing hero, security page, verify trust panel." },
];

// Brand-portal asset downloads. Cookie-gated, so when the API is on its own
// subdomain these become cross-origin links — apiUrl() prefixes the API base
// (the SameSite=None brand_portal cookie is sent on the cross-site GET).
const ASSET_PATHS = {
  logoSvg: apiUrl("/api/brand-portal/assets/logo.svg"),
  wordmarkSvg: apiUrl("/api/brand-portal/assets/wordmark.svg"),
  wordmarkStackedSvg: apiUrl("/api/brand-portal/assets/wordmark-stacked.svg"),
  paletteJson: apiUrl("/api/brand-portal/assets/palette.json"),
  tokensCss: apiUrl("/api/brand-portal/assets/tokens.css"),
  pdf: apiUrl("/api/brand-portal/assets/FortifyID-Brand-Guidelines.pdf"),
  zip: apiUrl("/api/brand-portal/assets/fortifyid-brand-kit.zip"),
};

export default function BrandPortal() {
  const status = useGetBrandPortalStatus();

  return (
    <>
      <Seo
        title="Brand portal"
        description="FortifyID brand assets, palette, typography, motion and downloads. Password-gated — direct-link access only."
        path="/brand"
        noindex
      />
      {status.isLoading ? (
        <Section>
          <div className="text-sm text-[hsl(var(--brand-slate))] font-mono">Loading…</div>
        </Section>
      ) : status.data?.unlocked ? (
        <Unlocked />
      ) : (
        <Locked disabled={status.data?.reason === "disabled"} />
      )}
    </>
  );
}

function Locked({ disabled }: { disabled: boolean }) {
  const qc = useQueryClient();
  const unlock = useUnlockBrandPortal();
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    setErrorMsg(null);
    unlock.mutate(
      { data: { password } },
      {
        onSuccess: (data) => {
          if (data?.unlocked) {
            qc.setQueryData(getGetBrandPortalStatusQueryKey(), { unlocked: true });
            qc.invalidateQueries({ queryKey: getGetBrandPortalStatusQueryKey() });
            setPassword("");
          } else {
            setErrorMsg("That password isn't right.");
          }
        },
        onError: (err) => {
          const status = (err as { status?: number } | null)?.status;
          if (status === 429) {
            setErrorMsg("Too many attempts. Try again in a few minutes.");
          } else if (status === 503) {
            setErrorMsg("The brand portal is currently disabled.");
          } else {
            setErrorMsg("That password isn't right.");
          }
        },
      },
    );
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[hsl(var(--brand-bone))] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-10">
          <Wordmark size="xl" />
        </div>
        <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-8 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <Eyebrow>Brand portal</Eyebrow>
          <h1 className="mt-3 text-2xl font-semibold text-[hsl(var(--brand-charcoal))] leading-tight">
            <LockKeyhole className="inline w-5 h-5 mr-2 -mt-1 text-[hsl(var(--brand-forest))]" />
            Enter password
          </h1>
          <p className="mt-3 text-sm text-[hsl(var(--brand-slate))] leading-relaxed">
            This portal is shared with partners, agencies and contractors. The password is in your invitation email.
          </p>
          {disabled ? (
            <div
              className="mt-6 rounded border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] px-4 py-3 text-sm text-[hsl(var(--brand-slate))]"
              role="status"
              aria-live="polite"
              data-testid="text-portal-disabled"
            >
              The brand portal is currently disabled. The site administrator hasn't set a portal password.
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="form-brand-unlock">
              <label className="block">
                <span className="sr-only">Password</span>
                <input
                  type="password"
                  autoFocus
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full rounded border border-[hsl(var(--brand-sand))] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-forest))]"
                  data-testid="input-brand-password"
                />
              </label>
              <div role="alert" aria-live="polite" className="min-h-[1.25rem]">
                {errorMsg && (
                  <p className="text-sm text-[hsl(var(--brand-slate))]" data-testid="text-brand-error">
                    {errorMsg}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={unlock.isPending || password.length === 0}
                className="w-full bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-4 py-2.5 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors disabled:opacity-60"
                data-testid="button-brand-enter"
              >
                {unlock.isPending ? "Checking…" : "Enter"}
              </button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-[hsl(var(--brand-slate))] font-mono tracking-wide">
          NOT FOR PUBLIC INDEXING
        </p>
      </div>
    </div>
  );
}

function Unlocked() {
  const qc = useQueryClient();

  // Read the CSRF token once when the unlocked view mounts (the fid_csrf
  // cookie is set by the server at the same time as the brand_portal session
  // cookie, so it is always present here).
  const csrfToken = useMemo(() => readCsrfToken(), []);

  const lock = useLockBrandPortal({
    request: { headers: { "X-CSRF-Token": csrfToken } } as RequestInit,
  });

  const onSignOut = () => {
    lock.mutate(undefined, {
      onSettled: () => {
        qc.setQueryData(getGetBrandPortalStatusQueryKey(), { unlocked: false });
        qc.invalidateQueries({ queryKey: getGetBrandPortalStatusQueryKey() });
      },
    });
  };

  return (
    <div className="bg-[hsl(var(--brand-bone))]">
      <PortalHeader onSignOut={onSignOut} signingOut={lock.isPending} />
      <CoverHero />
      <EssenceSection />
      <LogoSection />
      <ColourSection />
      <TypographySection />
      <MotionSection />
      <ApplicationsSection />
      <DownloadsSection />
      <PortalFooter />
    </div>
  );
}

function PortalHeader({ onSignOut, signingOut }: { onSignOut: () => void; signingOut: boolean }) {
  return (
    <div className="border-b border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))]">
      <div className="mx-auto max-w-6xl px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wordmark size="md" />
          <span className="hidden sm:inline font-mono text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">
            Brand portal
          </span>
        </div>
        <button
          onClick={onSignOut}
          disabled={signingOut}
          className="text-xs text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-charcoal))] flex items-center gap-1.5 disabled:opacity-60"
          data-testid="button-brand-signout"
        >
          <LogOut className="w-3.5 h-3.5" />
          {signingOut ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </div>
  );
}

function CoverHero() {
  return (
    <Section className="!pb-12 sm:!pb-16">
      <div className="rounded-lg overflow-hidden bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] relative">
        <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center px-8 py-14 sm:px-14 sm:py-20">
          <div className="max-w-xl">
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-brass))]">
              Brand portal · v1.0
            </div>
            <h1 className="mt-3 text-4xl sm:text-5xl font-semibold leading-[1.05]">
              FortifyID Brand
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[hsl(var(--brand-bone))]/85 leading-relaxed">
              Quiet, considered, trusted. Everything you need to render FortifyID accurately — palette, type, the shield, the motion, and the source files. Use it. Don't redraw it.
            </p>
          </div>
          <div className="w-44 sm:w-56 shrink-0 mx-auto md:mx-0">
            <AnimatedShield beat="data-flow" tone="hsl(var(--brand-bone))" cut="hsl(var(--brand-forest))" glow="hsl(var(--brand-brass))" />
          </div>
        </div>
      </div>
    </Section>
  );
}

function EssenceSection() {
  return (
    <Section>
      <SectionHeading number="01" eyebrow="Essence" title="Quiet, considered, trusted." />
      <div className="mt-10 grid md:grid-cols-2 gap-12">
        <div>
          <h3 className="text-sm font-semibold text-[hsl(var(--brand-charcoal))] uppercase tracking-wide">Voice</h3>
          <ul className="mt-4 space-y-3 text-sm text-[hsl(var(--brand-charcoal))] leading-relaxed">
            <li><strong className="font-semibold">Plain English.</strong> We don't say "leverage", "empower" or "unlock" — we say what the product does.</li>
            <li><strong className="font-semibold">Specific, never vague.</strong> Name the document, the form, the workflow, the law.</li>
            <li><strong className="font-semibold">Calm under pressure.</strong> Even when describing fraud, we sound like the senior partner in the room, not the alarm bell.</li>
            <li><strong className="font-semibold">Australian English.</strong> Colour, organisation, recognise. Always.</li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-[hsl(var(--brand-charcoal))] uppercase tracking-wide">Personality dial</h3>
          <div className="mt-4 space-y-3 font-mono text-xs text-[hsl(var(--brand-charcoal))]">
            <Dial left="Trusted" leftValue={8} right="Loud" rightValue={2} />
            <Dial left="Quiet" leftValue={8} right="Playful" rightValue={3} />
            <Dial left="Crafted" leftValue={7} right="Corporate" rightValue={4} />
            <Dial left="Specific" leftValue={9} right="Generic" rightValue={1} />
          </div>
        </div>
      </div>
    </Section>
  );
}

function Dial({ left, leftValue, right, rightValue }: { left: string; leftValue: number; right: string; rightValue: number }) {
  const fill = (n: number) => "█".repeat(n) + "░".repeat(10 - n);
  return (
    <div className="grid grid-cols-2 gap-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--brand-slate))] mb-1">{left}</div>
        <div className="text-[hsl(var(--brand-forest))]">{fill(leftValue)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--brand-slate))] mb-1">{right}</div>
        <div className="text-[hsl(var(--brand-slate))]">{fill(rightValue)}</div>
      </div>
    </div>
  );
}

function LogoSection() {
  return (
    <Section className="border-t border-[hsl(var(--brand-sand))] !pt-16">
      <SectionHeading number="02" eyebrow="Logo" title="The shield is the system." />
      <p className="mt-5 max-w-2xl text-base text-[hsl(var(--brand-slate))] leading-relaxed">
        The FortifyID mark is a Forest shield containing a Brass-cut chip — the silhouette of the encryption boundary that protects every change request. Use the lockup wherever there is room. Use the standalone shield only when the product context is already established.
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <LogoCard title="Lockup" downloadHref={ASSET_PATHS.wordmarkSvg} downloadName="wordmark.svg">
          <div className="flex items-center justify-center py-10">
            <Wordmark size="xl" />
          </div>
        </LogoCard>
        <LogoCard title="Stacked" downloadHref={ASSET_PATHS.wordmarkStackedSvg} downloadName="wordmark-stacked.svg">
          <div className="flex items-center justify-center py-10">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16">
                <AnimatedShield beat="static" />
              </div>
              <div className="font-display font-semibold text-2xl tracking-tight text-[hsl(var(--brand-charcoal))]">
                Fortify<span className="text-[hsl(var(--brand-forest))]">ID</span>
              </div>
            </div>
          </div>
        </LogoCard>
        <LogoCard title="Shield · light & reverse" downloadHref={ASSET_PATHS.logoSvg} downloadName="logo.svg">
          <div className="grid grid-cols-2">
            <div className="flex flex-col items-center justify-center gap-2 py-8 bg-[hsl(var(--brand-bone))] border-r border-[hsl(var(--brand-sand))]">
              <div className="w-24 h-24">
                <AnimatedShield beat="static" tone="hsl(var(--brand-forest))" cut="hsl(var(--brand-bone))" />
              </div>
              <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[hsl(var(--brand-slate))]">Light</div>
            </div>
            <div className="flex flex-col items-center justify-center gap-2 py-8 bg-[hsl(var(--brand-forest))]">
              <div className="w-24 h-24">
                <AnimatedShield beat="static" tone="hsl(var(--brand-bone))" cut="hsl(var(--brand-forest))" />
              </div>
              <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[hsl(var(--brand-bone))]/70">Reverse</div>
            </div>
          </div>
        </LogoCard>
        <LogoCard title="Shield · outline" downloadHref={ASSET_PATHS.logoSvg} downloadName="logo.svg">
          <div className="grid grid-cols-2">
            <div className="flex flex-col items-center justify-center gap-2 py-8 bg-[hsl(var(--brand-bone))] border-r border-[hsl(var(--brand-sand))]">
              <div className="w-24 h-24">
                <AnimatedShield beat="static" variant="outline" tone="hsl(var(--brand-forest))" cut="hsl(var(--brand-forest))" />
              </div>
              <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[hsl(var(--brand-slate))]">Outline</div>
            </div>
            <div className="flex flex-col items-center justify-center gap-2 py-8 bg-[hsl(var(--brand-forest))]">
              <div className="w-24 h-24">
                <AnimatedShield beat="static" variant="outline" tone="hsl(var(--brand-bone))" cut="hsl(var(--brand-bone))" />
              </div>
              <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[hsl(var(--brand-bone))]/70">Outline · reverse</div>
            </div>
          </div>
        </LogoCard>
        <LogoCard title="Clear space">
          <div className="flex items-center justify-center py-10 px-6">
            <div className="relative">
              <div className="absolute -inset-6 border border-dashed border-[hsl(var(--brand-brass))] opacity-60 rounded" />
              <div className="absolute -inset-6 flex items-center justify-center">
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[hsl(var(--brand-bone))] px-1 font-mono text-[9px] tracking-[0.18em] uppercase text-[hsl(var(--brand-slate))]">x = chip height</span>
              </div>
              <div className="w-20 h-20 relative">
                <AnimatedShield beat="static" />
              </div>
            </div>
          </div>
        </LogoCard>
      </div>

      <h3 className="mt-14 text-sm font-semibold text-[hsl(var(--brand-charcoal))] uppercase tracking-wide">Don't</h3>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <DontTile label="Don't recolour" badStyle={{ filter: "hue-rotate(140deg) saturate(2)" }} />
        <DontTile label="Don't gradient" badStyle={{ background: "linear-gradient(135deg, #ff6a00, #ee0979)", borderRadius: 6 }} />
        <DontTile label="Don't rotate" badStyle={{ transform: "rotate(18deg)" }} />
        <DontTile label="Don't mix typefaces" altWordmark />
      </div>
    </Section>
  );
}

function LogoCard({ title, children, downloadHref, downloadName }: { title: string; children: React.ReactNode; downloadHref?: string; downloadName?: string }) {
  return (
    <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white">
      <div className="border-b border-[hsl(var(--brand-sand))] px-4 py-2.5 flex items-center justify-between">
        <span className="text-xs font-medium text-[hsl(var(--brand-charcoal))]">{title}</span>
        {downloadHref && (
          <a
            href={downloadHref}
            download={downloadName}
            className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--brand-forest))] hover:underline font-mono"
            data-testid={`link-download-${downloadName}`}
          >
            <Download className="w-3 h-3" />
            SVG
          </a>
        )}
      </div>
      <div className="bg-[hsl(var(--brand-bone))]/40">
        {children}
      </div>
    </div>
  );
}

function DontTile({ label, badStyle, altWordmark }: { label: string; badStyle?: React.CSSProperties; altWordmark?: boolean }) {
  return (
    <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-6 flex flex-col items-center gap-4">
      <div className="w-16 h-16 relative" style={badStyle}>
        {altWordmark ? (
          <span style={{ fontFamily: "'Comic Sans MS', cursive", fontSize: 14, color: "#1E4A40", display: "inline-block", marginTop: 24 }}>FortifyID</span>
        ) : (
          <AnimatedShield beat="static" />
        )}
      </div>
      <div className="text-xs text-[hsl(var(--brand-slate))] font-mono">{label}</div>
    </div>
  );
}

function ColourSection() {
  return (
    <Section className="border-t border-[hsl(var(--brand-sand))] !pt-16">
      <SectionHeading number="03" eyebrow="Colour" title="Six tokens. No exceptions." />
      <p className="mt-5 max-w-2xl text-base text-[hsl(var(--brand-slate))] leading-relaxed">
        The Quiet Tech palette ships as CSS custom properties. Every product surface, marketing page, email and document references these tokens directly.
      </p>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SWATCHES.map((s) => (
          <Swatch key={s.name} swatch={s} />
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-3">
        <DownloadButton href={ASSET_PATHS.paletteJson} name="palette.json">Download palette.json</DownloadButton>
        <DownloadButton href={ASSET_PATHS.tokensCss} name="tokens.css">Download tokens.css</DownloadButton>
      </div>
    </Section>
  );
}

function Swatch({ swatch }: { swatch: typeof SWATCHES[number] }) {
  return (
    <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white overflow-hidden">
      <div className="h-28" style={{ backgroundColor: swatch.hex }} />
      <div className="p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-[hsl(var(--brand-charcoal))]">{swatch.name}</span>
        </div>
        <CopyRow label="HEX" value={swatch.hex} />
        <CopyRow label="HSL" value={swatch.hsl} />
        <CopyRow label="Token" value={swatch.token} />
      </div>
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // clipboard unavailable — silently no-op
    }
  };
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--brand-slate))] w-12 shrink-0">{label}</span>
        <span className="font-mono text-xs text-[hsl(var(--brand-charcoal))] truncate">{value}</span>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-forest))] shrink-0"
        aria-label={`Copy ${label} ${value}`}
      >
        {copied ? <Check className="w-3.5 h-3.5 text-[hsl(var(--brand-forest))]" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

function TypographySection() {
  return (
    <Section className="border-t border-[hsl(var(--brand-sand))] !pt-16">
      <SectionHeading number="04" eyebrow="Typography" title="Space Grotesk · Inter · JetBrains Mono." />
      <p className="mt-5 max-w-2xl text-base text-[hsl(var(--brand-slate))] leading-relaxed">
        Three typefaces, each with a single job. We never mix them inside a single line of copy. All three are loaded from{" "}
        <a href="https://fonts.google.com/specimen/Space+Grotesk" className="underline" target="_blank" rel="noreferrer">Google</a>{" "}
        <a href="https://fonts.google.com/specimen/Inter" className="underline" target="_blank" rel="noreferrer">Fonts</a>{" "}
        (<a href="https://fonts.google.com/specimen/JetBrains+Mono" className="underline" target="_blank" rel="noreferrer">JetBrains Mono</a>).
      </p>

      <div className="mt-10 space-y-10">
        <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-8">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">Space Grotesk · display</div>
          <p className="font-display font-semibold text-4xl sm:text-5xl text-[hsl(var(--brand-charcoal))] leading-[1.02] tracking-tight mt-3">
            Stop trust-account fraud at the source.
          </p>
          <div className="font-mono text-xs text-[hsl(var(--brand-slate))] mt-4">Semibold · −1.5% tracking · 1.02 line-height</div>
        </div>

        <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-8">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">Inter · body / UI</div>
          <p className="text-base text-[hsl(var(--brand-charcoal))] leading-relaxed mt-3 max-w-2xl">
            FortifyID lets your agency issue identity-verified change requests to owners, tenants and vendors — replacing email and PDF forms with a co-branded, auditable flow.
          </p>
          <div className="font-mono text-xs text-[hsl(var(--brand-slate))] mt-4">Regular / Medium · 0% tracking · 1.6 line-height</div>
        </div>

        <div className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-8">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">JetBrains Mono · eyebrow / code</div>
          <p className="font-mono text-sm text-[hsl(var(--brand-brass))] mt-3 tracking-[0.18em] uppercase">
            BSB · ACCT · ID-VERIFIED · AUDIT-READY
          </p>
          <div className="font-mono text-xs text-[hsl(var(--brand-slate))] mt-4">Medium · uppercase · letter-spaced for eyebrows</div>
        </div>
      </div>

      <h3 className="mt-12 text-sm font-semibold text-[hsl(var(--brand-charcoal))] uppercase tracking-wide">Hierarchy</h3>
      <div className="mt-4 overflow-x-auto rounded-lg border border-[hsl(var(--brand-sand))] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--brand-bone))] text-left">
            <tr>
              <Th>Role</Th><Th>Family</Th><Th>Size</Th><Th>Leading</Th><Th>Tracking</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--brand-sand))]">
            <Tr cols={["H1 hero", "Space Grotesk Semibold", "48–60 px", "1.02", "−1.5%"]} />
            <Tr cols={["H2 section", "Space Grotesk Semibold", "28–36 px", "1.10", "−1.5%"]} />
            <Tr cols={["Body", "Inter Regular", "16–18 px", "1.60", "0%"]} />
            <Tr cols={["Eyebrow", "JetBrains Mono Medium", "11 px", "1.20", "+0.18em uppercase"]} />
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--brand-slate))]">{children}</th>;
}
function Tr({ cols }: { cols: string[] }) {
  return (
    <tr>
      {cols.map((c, i) => (
        <td key={i} className="px-4 py-3 text-sm text-[hsl(var(--brand-charcoal))]">{c}</td>
      ))}
    </tr>
  );
}

function MotionSection() {
  return (
    <Section className="border-t border-[hsl(var(--brand-sand))] !pt-16">
      <SectionHeading number="05" eyebrow="Motion · The animated shield" title="Data flows in. Data is encrypted." />
      <p className="mt-5 max-w-2xl text-base text-[hsl(var(--brand-slate))] leading-relaxed">
        The animated shield is FortifyID's signature motion. Labelled packets — BSB, ACCT, EMAIL, PHONE, ADDR, ID#, ABN, NAME — ride the chip's traces inward, scramble into hex ciphertext as they cross the chip frame, and land in the die as bullets while a small lock flashes acknowledgement.
      </p>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {MOTION_BEATS.map((b) => (
          <div key={b.beat} className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white overflow-hidden">
            <div className="grid grid-cols-2">
              <div className="bg-[hsl(var(--brand-bone))] flex items-center justify-center p-5 border-r border-[hsl(var(--brand-sand))]" title="On light surfaces">
                <div className="w-16 h-16">
                  <AnimatedShield beat={b.beat} tone="hsl(var(--brand-forest))" cut="hsl(var(--brand-bone))" glow="hsl(var(--brand-brass))" />
                </div>
              </div>
              <div className="bg-[hsl(var(--brand-forest))] flex items-center justify-center p-5" title="On dark surfaces (reverse)">
                <div className="w-16 h-16">
                  <AnimatedShield beat={b.beat} tone="hsl(var(--brand-bone))" cut="hsl(var(--brand-forest))" glow="hsl(var(--brand-brass))" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 border-t border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))]/40">
              <div className="px-3 py-1.5 font-mono text-[9px] tracking-[0.16em] uppercase text-[hsl(var(--brand-slate))] border-r border-[hsl(var(--brand-sand))] text-center">Light</div>
              <div className="px-3 py-1.5 font-mono text-[9px] tracking-[0.16em] uppercase text-[hsl(var(--brand-slate))] text-center">Reverse</div>
            </div>
            <div className="p-4">
              <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[hsl(var(--brand-slate))]">{b.label}</div>
              <p className="mt-2 text-xs text-[hsl(var(--brand-charcoal))] leading-relaxed">{b.caption}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 text-xs text-[hsl(var(--brand-slate))] font-mono uppercase tracking-[0.16em]">
        Both directions are valid — pick by surface contrast.
      </div>

      <div className="mt-10 rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-6">
        <h3 className="text-sm font-semibold text-[hsl(var(--brand-charcoal))] uppercase tracking-wide">Rules</h3>
        <ul className="mt-3 space-y-2 text-sm text-[hsl(var(--brand-charcoal))] leading-relaxed">
          <li>Always honour <code className="font-mono text-xs">prefers-reduced-motion</code>. The component does this automatically — never re-enable animation in code that overrides it.</li>
          <li>Only one animated shield per viewport. Multiple beats compete for attention and break the calm.</li>
          <li>Never recolour the packets. Brass on Bone, on a Forest (or co-branded primary) panel.</li>
        </ul>
      </div>
    </Section>
  );
}

function ApplicationsSection() {
  const apps = useMemo(
    () => [
      { name: "Marketing site", path: "fortifyid at /", body: "Bone background, Forest hero panels, Brass accents. Full-bleed AnimatedShield in data-flow mode in the hero." },
      { name: "Staff Hub", path: "/app/", body: "Workspace-scoped product surface. Bone canvas, Forest sidebar, Brass focus rings. Ambient shield in the sidebar lockup, handshake on landing." },
      { name: "Verify", path: "/verify/:slug/:token", body: "Public co-branded client surface. The agency's --brand-primary replaces Forest. The animated shield re-tints to the agency colour. No FortifyID branding in front of the agency's client." },
      { name: "Operations console", path: "/ops", body: "FortifyID-only. Charcoal sidebar instead of Forest to differentiate from agency surfaces. Brass accents preserved." },
      { name: "Native mobile", path: "staff-mobile", body: "Forest splash + sign-in hero, Bone canvas, Brass accents. Native Shield component mirrors the web AnimatedShield with the data-flow beat on splash/sign-in." },
    ],
    [],
  );
  return (
    <Section className="border-t border-[hsl(var(--brand-sand))] !pt-16">
      <SectionHeading number="06" eyebrow="Applications" title="Where the brand lives." />
      <p className="mt-5 max-w-2xl text-base text-[hsl(var(--brand-slate))] leading-relaxed">
        FortifyID ships across five surfaces. Each one uses the same six tokens, the same three typefaces and the same shield — only the density and the chrome change.
      </p>
      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {apps.map((a) => (
          <div key={a.name} className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-5">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">{a.path}</div>
            <h3 className="mt-2 text-base font-semibold text-[hsl(var(--brand-charcoal))]">{a.name}</h3>
            <p className="mt-2 text-sm text-[hsl(var(--brand-charcoal))] leading-relaxed">{a.body}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function DownloadsSection() {
  return (
    <Section className="border-t border-[hsl(var(--brand-sand))] !pt-16">
      <SectionHeading number="07" eyebrow="Downloads" title="Take it with you." />
      <p className="mt-5 max-w-2xl text-base text-[hsl(var(--brand-slate))] leading-relaxed">
        Source files for the lockup, palette, tokens and full guidelines. Or grab everything in one ZIP.
      </p>

      <div className="mt-10 rounded-lg bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-brass))]">Brand kit</div>
          <h3 className="mt-2 text-2xl font-semibold">FortifyID brand kit (.zip)</h3>
          <p className="mt-2 text-sm text-[hsl(var(--brand-bone))]/80">Logo, both wordmarks, palette JSON, CSS tokens and the full guidelines PDF — assembled at request time so it's always current.</p>
        </div>
        <a
          href={ASSET_PATHS.zip}
          download="fortifyid-brand-kit.zip"
          className="inline-flex items-center gap-2 bg-[hsl(var(--brand-brass))] text-[hsl(var(--brand-charcoal))] px-5 py-3 rounded font-medium hover:opacity-90 whitespace-nowrap"
          data-testid="link-download-zip"
        >
          <Download className="w-4 h-4" />
          Download brand kit
        </a>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <DownloadCard href={ASSET_PATHS.logoSvg} name="logo.svg" desc="Standalone shield mark" />
        <DownloadCard href={ASSET_PATHS.wordmarkSvg} name="wordmark.svg" desc="Horizontal lockup" />
        <DownloadCard href={ASSET_PATHS.wordmarkStackedSvg} name="wordmark-stacked.svg" desc="Stacked lockup" />
        <DownloadCard href={ASSET_PATHS.paletteJson} name="palette.json" desc="Six-token colour palette" />
        <DownloadCard href={ASSET_PATHS.tokensCss} name="tokens.css" desc=":root CSS custom props" />
        <DownloadCard href={ASSET_PATHS.pdf} name="FortifyID-Brand-Guidelines.pdf" desc="Full PDF guidelines" />
      </div>
    </Section>
  );
}

function DownloadCard({ href, name, desc }: { href: string; name: string; desc: string }) {
  return (
    <a
      href={href}
      download={name}
      className="rounded-lg border border-[hsl(var(--brand-sand))] bg-white p-4 hover:border-[hsl(var(--brand-forest))] transition-colors group"
      data-testid={`link-download-${name}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-[hsl(var(--brand-charcoal))] truncate">{name}</div>
          <div className="text-xs text-[hsl(var(--brand-slate))] mt-1">{desc}</div>
        </div>
        <Download className="w-4 h-4 text-[hsl(var(--brand-slate))] group-hover:text-[hsl(var(--brand-forest))] shrink-0" />
      </div>
    </a>
  );
}

function DownloadButton({ href, name, children }: { href: string; name: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      download={name}
      className="inline-flex items-center gap-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-4 py-2 rounded text-sm font-medium hover:bg-[hsl(168,25%,22%)] transition-colors"
      data-testid={`link-download-${name}`}
    >
      <Download className="w-3.5 h-3.5" />
      {children}
    </a>
  );
}

function PortalFooter() {
  return (
    <div className="border-t border-[hsl(var(--brand-sand))]">
      <div className="mx-auto max-w-6xl px-6 py-8 flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">Brand portal · v1.0</span>
        <a href="/contact?topic=other" className="font-mono text-xs text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-forest))]">Brand questions → contact form</a>
      </div>
    </div>
  );
}

function SectionHeading({ number, eyebrow, title }: { number: string; eyebrow: string; title: string }) {
  return (
    <div>
      <Eyebrow>{number} · {eyebrow}</Eyebrow>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-[hsl(var(--brand-charcoal))] leading-[1.1] tracking-tight">{title}</h2>
    </div>
  );
}
